import React, { useMemo } from "react";

/**
 * Pure-SVG 7-day sparkline — zero chart dependencies on first paint.
 * Downsamples the 168 hourly points to ≤ 42 points, then memoizes the
 * polyline path string so row re-renders never recompute geometry.
 */
export default function Sparkline({ data, up = true, width = 120, height = 36 }) {
  const { line, area } = useMemo(() => {
    const pts = Array.isArray(data) ? data : [];
    if (pts.length < 2) return { line: "", area: "" };

    // Downsample to at most 42 points for a smooth but cheap path.
    const step = Math.max(1, Math.ceil(pts.length / 42));
    const values = pts.filter((_, i) => i % step === 0);
    if (values[values.length - 1] !== pts[pts.length - 1]) {
      values.push(pts[pts.length - 1]);
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const pad = 2;

    const coords = values.map((v, i) => {
      const x = (i / (values.length - 1)) * (width - pad * 2) + pad;
      const y =
        height - pad - ((v - min) / range) * (height - pad * 2);
      return [x, y];
    });

    const lineStr = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const areaStr = `${pad},${height - pad} ${lineStr} ${width - pad},${height - pad}`;
    return { line: lineStr, area: areaStr };
  }, [data, width, height]);

  if (!line) {
    return <div className="sparkline-empty" aria-hidden="true" />;
  }

  const stroke = up ? "var(--emerald)" : "var(--red)";

  return (
    <svg
      className="sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={up ? "Upward 7-day trend" : "Downward 7-day trend"}
    >
      <polygon points={area} fill={up ? "var(--emerald-soft)" : "var(--red-soft)"} />
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
